/**
 * Emergency Validation Panel Component (AC-010 / AC-012)
 *
 * - Shows completeness score as a progress bar
 * - Lists warnings colour-coded by severity (critical=red, high=orange, medium=yellow, low=blue)
 * - Clicking a warning navigates to the relevant data-entry page
 */

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import {
  ValidationResult,
  ValidationWarning,
} from "../../services/emergency.service";

interface Props {
  validation: ValidationResult | null;
  loading?: boolean;
  onRefresh?: () => void;
}

const SEVERITY_CONFIG = {
  critical: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-800",
    badge: "bg-red-100 text-red-700",
    icon: AlertCircle,
    iconClass: "text-red-600",
  },
  high: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-800",
    badge: "bg-orange-100 text-orange-700",
    icon: AlertTriangle,
    iconClass: "text-orange-500",
  },
  medium: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-800",
    badge: "bg-yellow-100 text-yellow-700",
    icon: AlertTriangle,
    iconClass: "text-yellow-500",
  },
  low: {
    bg: "bg-primary-50",
    border: "border-primary-200",
    text: "text-primary-800",
    badge: "bg-primary-100 text-primary-700",
    icon: Info,
    iconClass: "text-primary-500",
  },
};

/** Maps a warning field name to the most relevant edit route */
function fieldToRoute(field: string): string {
  const map: Record<string, string> = {
    allergies: "/traveler/health",
    medications: "/traveler/medications",
    conditions: "/traveler/health",
    devices: "/traveler/health",
    emergencyContacts: "/traveler/health",
    demographics: "/traveler/profile",
    patient: "/traveler/profile",
    immunizations: "/traveler/immunizations",
    labResults: "/traveler/health",
    procedures: "/traveler/health",
  };
  return map[field] || "/traveler/health";
}

/** Severity codes stay as sent by the backend; only the heading is translated. */
function severityLabel(
  severity: ValidationWarning["severity"],
  t: Translation,
): string {
  const labels: Record<string, string> = {
    critical: t.emergxSevCritical,
    high: t.emergxSevHigh,
    medium: t.emergxSevMedium,
    low: t.emergxSevLow,
  };
  return labels[severity] ?? severity;
}

function scoreBarColor(score: number): string {
  if (score >= 80) return "bg-green-500";
  if (score >= 50) return "bg-yellow-400";
  return "bg-red-500";
}

const EmergencyValidationPanel: React.FC<Props> = ({
  validation,
  loading,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-24 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> {t.emergxValidating}
      </div>
    );
  }

  if (!validation) {
    return (
      <div className="text-center py-6 text-gray-400 text-sm">
        {t.emergxNoValidationData}{" "}
        <button onClick={onRefresh} className="text-primary-600 underline">
          {t.refresh}
        </button>
      </div>
    );
  }

  const grouped = ["critical", "high", "medium", "low"]
    .map((sev) => ({
      severity: sev as ValidationWarning["severity"],
      warnings: validation.warnings.filter((w) => w.severity === sev),
    }))
    .filter((g) => g.warnings.length > 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900">
            {t.emergxValidationTitle}
          </h3>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> {t.emergxRevalidate}
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{t.emergxCompleteness}</span>
          <span
            className={`font-semibold ${
              validation.completenessScore >= 80
                ? "text-green-700"
                : validation.completenessScore >= 50
                  ? "text-yellow-700"
                  : "text-red-700"
            }`}
          >
            {validation.completenessScore}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${scoreBarColor(validation.completenessScore)}`}
            style={{ width: `${validation.completenessScore}%` }}
          />
        </div>
        {validation.isValid && (
          <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5" /> {t.emergxNoCriticalIssues}
          </p>
        )}
      </div>

      {/* Warning groups */}
      {grouped.length === 0 ? (
        <div className="flex items-center gap-2 text-green-700 text-sm bg-green-50 rounded-lg px-4 py-3 border border-green-200">
          <CheckCircle className="w-4 h-4" /> {t.emergxAllChecksPassed}
        </div>
      ) : (
        grouped.map(({ severity, warnings }) => {
          const cfg = SEVERITY_CONFIG[severity];
          const Icon = cfg.icon;
          return (
            <div
              key={severity}
              className={`rounded-lg border ${cfg.border} ${cfg.bg} overflow-hidden`}
            >
              <div className="flex items-center gap-2 px-4 py-2 border-b border-inherit">
                <Icon className={`w-4 h-4 ${cfg.iconClass}`} />
                <span
                  className={`text-xs font-semibold uppercase tracking-wide ${cfg.text}`}
                >
                  {severityLabel(severity, t)} ({warnings.length})
                </span>
              </div>
              <ul className="divide-y divide-inherit">
                {warnings.map((w, i) => (
                  <li key={i} className={`px-4 py-3 ${cfg.bg}`}>
                    <button
                      className="w-full text-left"
                      onClick={() => navigate(fieldToRoute(w.field))}
                    >
                      <p className={`text-sm font-medium ${cfg.text}`}>
                        {w.message}
                      </p>
                      {w.recommendation && (
                        <p className={`text-xs mt-0.5 opacity-75 ${cfg.text}`}>
                          {w.recommendation}
                        </p>
                      )}
                      <p
                        className={`text-xs mt-1 underline opacity-60 ${cfg.text}`}
                      >
                        →{" "}
                        {t.emergxFixIn.replace(
                          "{route}",
                          fieldToRoute(w.field),
                        )}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}

      {validation.lastValidated && (
        <p className="text-xs text-gray-400 text-right">
          {t.emergxLastValidated.replace(
            "{date}",
            fmt.dateTime(validation.lastValidated),
          )}
        </p>
      )}
    </div>
  );
};

export default EmergencyValidationPanel;
